<template>
  <div>
    <div
      v-if="loading"
      class="fixed z-10 grid top-54 left-108 place-items-center"
      :style="{
        background: 'rgba(255,255,255, 0.5)',
        width: 'calc(100vw - 22rem - 5rem - 0.5rem)',
        height: 'calc(100vh - 13.5rem - 0.5rem)'
      }"
    >
      <div class="blum-loader"></div>
    </div>

    <div
      v-if="showOrphanBenner"
      class="sticky z-20 flex items-center justify-between w-full px-4 h-14 top-42 bg-tomato-200"
    >
      <div class="sticky z-10 flex items-center gap-4 left-92">
        <div
          class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded bg-tomato-300"
        >
          <BlIcon icon="warning" color="tomato-7" class="w-6 h-6" />
        </div>
        <div>
          <BlTextBody2
            :text="`${totalOrphans} appointment(s)`"
            class="font-bold text-tomato-800"
          />
          <BlTextBody2
            text="don't have a therapist assigned. Please update them."
            class="text-tomato-800"
          />
        </div>
      </div>
      <div class="sticky z-10 right-4">
        <router-link
          to="/appointments?page=1&per_page=25&status=orphan&preview_type=list"
          target="_blank"
        >
          <BlInputButton type="error" label="View appointment" dense />
        </router-link>
      </div>
    </div>

    <div
      class="sticky z-20 flex h-12 bg-slate-200"
      :class="[showOrphanBenner ? 'top-56' : 'top-42']"
    >
      <div
        class="sticky z-10 flex-shrink-0 w-20 h-12 border-b left-88 bg-slate-200 border-slate-400"
      ></div>
      <div
        v-for="(n, idx) in headers"
        :key="idx"
        class="flex items-center justify-center flex-shrink-0 h-12 px-2 py-1 border-b border-slate-400"
        :style="{ width: `calc(${slotWidth})` }"
      >
        <div v-if="n.highlight" class="flex items-center gap-1 h-7">
          <BlTextBody2 :text="n.label.split(' ')[0]" />
          <div
            class="flex items-center justify-center rounded-full h-7 w-7 bg-light-purple-400"
          >
            <BlTextBody2
              :text="n.label.split(' ').pop()"
              class="text-pure-white"
            />
          </div>
        </div>
        <div
          v-else-if="enableBranch && n.branch"
          class="flex items-center justify-center px-2 truncate rounded h-7"
          :style="{ backgroundColor: n.branch.background_color || 'unset' }"
          :title="`${n.label} - ${n.branch.name}`"
        >
          <BlTextBody2
            class="truncate whitespace-no-wrap"
            :text="n.label"
            :style="{ color: n.branch.color || 'unset' }"
          />
        </div>
        <div v-else class="px-4 truncate h-7" :title="n.label">
          <BlTextBody2 :text="n.label" class="truncate" />
        </div>
      </div>
    </div>

    <div class="relative flex flex-col bg-slate-200">
      <div
        v-for="time in clockRound"
        :key="time"
        class="flex"
        :id="`time_view_${time}`"
      >
        <div
          class="sticky z-10 flex justify-end flex-shrink-0 w-20 px-3 py-2 transition-all duration-300 border-b border-r left-88 bg-slate-200 border-slate-400"
          :style="{ height: `${slotHeight * 2}rem` }"
        >
          <BlTextBody2 :text="time" />
        </div>
        <div
          v-for="(n, idx) in headers"
          :key="idx"
          class="flex flex-col items-center justify-center flex-shrink-0 gap-2 transition-all duration-300 border-b border-r border-slate-400 bg-slate-200"
          :style="{
            height: `${slotHeight * 2}rem`,
            width: `calc(${slotWidth})`
          }"
        ></div>
      </div>
      <div
        v-if="!loading"
        class="absolute flex items-center"
        :style="{
          opacity: currentTime.opacity,
          top: currentTime.top,
          left: `calc(${currentTime.left})`,
          zIndex: 1
        }"
      >
        <div class="flex-shrink-0 w-2 h-2 rounded bg-red-cherry"></div>
        <div
          class="h-1 bg-red-cherry"
          :style="{ width: `calc(${currentTime.width})` }"
        ></div>
      </div>
      <AppointmentsAvailableTimeslot
        v-for="(item, idx) in availableTimeslots"
        :key="idx"
        :isCompact="isCompact"
        :item="item"
        :headers="headers"
        :slotHeight="slotHeight"
        :slotWidth="slotWidth"
        :selectedSlots="selectedSlots"
        @add-slot="onAddSlot"
        @resume-booking="onResumeBooking"
        @after-commit="$emit('after-commit')"
      />
      <OverlappedTimeslot
        v-for="(item, idx) in overlappedTimeslots"
        :key="`overlapped-${idx}`"
        :isCompact="isCompact"
        :item="item"
        :headers="headers"
        :slotHeight="slotHeight"
        :slotWidth="slotWidth"
        @after-commit="$emit('after-commit')"
      />

      <div
        class="fixed z-50 flex items-center justify-between h-16 px-6 transition-all duration-500 bg-white border rounded shadow-lg w-200 border-slate-400"
        :class="[selectedSlots.length ? 'bottom-16' : '-bottom-16']"
        :style="{
          left: '50%',
          transform: 'translateX(-50%)'
        }"
      >
        <div class="flex items-center gap-2">
          <BlInputButton type="plain" icon="close" @clicked="onRemoveSlots" />
          <div>
            <BlTextSubtitle3
              :text="`Schedule appointment for ${selectedTime}`"
              class="text-slate-800"
            />
            <BlTextSubtitle3
              v-if="selectedAvailability"
              :text="
                `, ${displayDate(selectedAvailability.date, 'DD MMM YYYY')}`
              "
              class="text-slate-800"
            />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <BlInputButton
            dense
            label="Single"
            @clicked="newSingleOpened = true"
          />
          <BlInputButton
            dense
            label="Recurring"
            @clicked="newRecurringOpened = true"
          />
        </div>
      </div>
    </div>

    <NewSingleAppointmentOverlay
      v-if="newSingleOpened && currentUser.role.includes('admin')"
      :availability="selectedAvailability"
      :slots="selectedSlots"
      @close-modal="newSingleOpened = false"
      @after-commit="$emit('after-commit')"
    />
    <NewRecurringAppointmentOverlay
      v-if="newRecurringOpened && currentUser.role.includes('admin')"
      :availability="selectedAvailability"
      :slots="selectedSlots"
      :bookingId="selectedBookingId"
      @close-modal="onCloseNewRecurring"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { CLOCK_ROUND, CLOCK_HALF } from "@/plugins/assets.js";
import moment from "moment";

import AppointmentsAvailableTimeslot from "./AvailableTimeslot.vue";
import OverlappedTimeslot from "./OverlappedTimeslot.vue";
import NewSingleAppointmentOverlay from "../overlay/NewSingleAppointmentOverlay.vue";
import NewRecurringAppointmentOverlay from "../overlay/NewRecurringAppointmentOverlay.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "AppointmentsTimeslots",
  components: {
    AppointmentsAvailableTimeslot,
    OverlappedTimeslot,
    NewSingleAppointmentOverlay,
    NewRecurringAppointmentOverlay,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputButton,
    BlIcon
  },
  props: {
    isCompact: { type: Boolean, default: false },
    selectedTherapist: { required: false, default: null },
    selectedRoom: { required: false, default: null },
    selectedDate: { required: false, default: null },
    headers: { type: Array, required: true },
    loading: { type: Boolean, default: false }
    // @after-commit
  },
  data() {
    return {
      selectedAvailability: null,
      selectedSlots: [],
      selectedBookingId: 0,
      newSingleOpened: false,
      newRecurringOpened: false,
      clockRound: CLOCK_ROUND,
      clockHalf: CLOCK_HALF,
      //
      showOrphanBenner: false,
      totalOrphans: 0
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "timeslots"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    dimension() {
      const searchParams = this.$route.query;
      return searchParams["type"];
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.isCompact) return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      if (this.headers.length > 7) return "12rem";
      else return `(100vw - 22rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    currentTime() {
      let opacity = "100%";
      const full = `${this.slotWidth} * ${this.headers.length}`;
      const oneSlot = this.slotWidth;
      let width = "0px";

      const hours = moment().format("H");
      const minutes = moment().format("mm");
      const timeInMinutes = Number(hours) * 60 + Number(minutes);
      const top = (timeInMinutes / 60) * (this.slotHeight * 2) + "rem";

      let left = `5rem`;
      if (this.dimension === "daily") {
        width = full + " - 0.5rem";
        const isSameDay =
          moment().format("YYYY-MM-DD") ===
          moment(this.selectedDate).format("YYYY-MM-DD");
        if (!isSameDay) opacity = "0%";
      }

      if (this.dimension === "weekly") {
        width = oneSlot + " - 0.5rem";
        const indexOfToday = this.headers.findIndex(i => i.highlight);
        if (indexOfToday > -1) {
          const loop = indexOfToday;
          for (let index = 0; index < loop; index++) {
            left += ` + ${oneSlot}`;
          }
        } else opacity = "0%";
      }

      return { opacity, width, top, left };
    },
    /**
    {
      "availability_id": 70,
      "key": ..., // as room or date { "id": "2024-08-15", "name": "2024-08-15" }
      "unique": "1_70", // combination
      "therapist": { "id": 9, "name": "Seliya" },
      "user": { "id": 9, "name": "Seliya" },
      "room": {
        "id": 1,
        "name": "Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek",
        "branch": { "id": 1, "name": "East", "color": "#C73D3D", "background_color": "#FBE5E5" }
      },
      "date": "2024-08-16",
      "start_time_string": "08:00",
      "end_time_string": "10:00",
      "primary_color": "#0284C7",
      "secondary_color": "#E0F2FE",
      "slots": [
        {
          "id": 311,
          "availability_id": 70,
          "start_datetime": "2024-08-16T01:00:00.000Z",
          "end_datetime": "2024-08-16T01:30:00.000Z",
          "date": "2024-08-16",
          "start_time": "2000-01-01T08:00:00.000Z",
          "end_time": "2000-01-01T08:30:00.000Z",
          "created_at": "2024-08-15T02:30:30.624Z",
          "updated_at": "2024-08-15T02:30:30.624Z",
          "start_time_string": "08:00",
          "end_time_string": "08:30",
          "primary_color": "#0284C7",
          "secondary_color": "#E0F2FE",
          "is_booked": false,
          "appointment_id": null,
          "appointment": null,
          "availability": {
            "user_id": 9,
            "room_id": 1,
            "user_name": "Seliya",
            "room_name": "Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek",
            "room": {
              "id": 1,
              "name": "Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek Anggrek",
              "branch": {
                "id": 1,
                "name": "East",
                "color": "#C73D3D",
                "background_color": "#FBE5E5"
              }
            }
          }
        },
      ]
    }
    */
    availableTimeslots() {
      const arr = [];
      const keyHeaders = this.headers.map(i => i.value);

      this.timeslots.forEach(body => {
        const date = body.date ? body.date : null;
        const room = body.room ? body.room : null;
        const key = date ? { id: date, name: date } : room;

        body.timeslots.forEach(slot => {
          const unique = `${key.id}_${slot.availability_id}`;
          const therapist = {
            id: slot.availability.user_id,
            name: slot.availability.user_name
          };
          const room = slot.availability.room;
          const usedDate =
            date || moment(this.selectedDate).format("YYYY-MM-DD");

          const lastIndex = arr.findIndex(i => i.unique === unique);
          if (lastIndex > -1) {
            arr[lastIndex].end_time_string = slot.end_time_string;
            arr[lastIndex].slots.push({ ...slot, unique });
          } else {
            const res = {
              key,
              unique,
              therapist,
              user: therapist,
              room,
              date: usedDate,
              //
              availability_id: slot.availability_id,
              start_time_string: slot.start_time_string,
              end_time_string: slot.end_time_string,
              primary_color: slot.primary_color,
              secondary_color: slot.secondary_color,
              slots: [{ ...slot, unique }]
            };
            arr.push(res);
          }
        });
      });
      return arr.filter(i => keyHeaders.includes(i.key.id));
    },
    overlappedTimeslots() {
      const arr = [];
      const keyHeaders = this.headers.map(i => i.value);
      this.timeslots.forEach(body => {
        const date = body.date ? body.date : null;
        const room = body.room ? body.room : null;
        const key = body.date ? { id: date, name: date } : room;
        if (body.overlapped) {
          body.overlapped.forEach(slot => {
            const usedDate =
              date || moment(this.selectedDate).format("YYYY-MM-DD");
            const availabilityRoom = slot.timeslots.length
              ? slot.timeslots[0].availability.room
              : null;
            const useRoom = room || availabilityRoom;

            const res = {
              key,
              room: useRoom,
              date: usedDate,
              start_time: slot.start_time,
              end_time: slot.end_time,
              timeslots: slot.timeslots
            };
            arr.push(res);
          });
        }
      });
      return arr.filter(i => keyHeaders.includes(i.key.id));
    },
    selectedTime() {
      if (!this.selectedSlots.length) return "";
      const l = this.selectedSlots.length - 1;
      const start = this.selectedSlots[0].start_time_string;
      const end = this.selectedSlots[l].end_time_string;
      return `${start} - ${end}`;
    }
  },
  watch: {
    newSingleOpened(val) {
      if (!val) this.onRemoveSlots();
    },
    newRecurringOpened(val) {
      if (!val) this.onRemoveSlots();
    }
  },
  mounted() {
    this.fetchOrphanAppointments();
    setTimeout(() => {
      this.onScrollToTime("07:00");
    }, 1000);
  },
  methods: {
    ...mapActions(["showToast", "actionGet"]),
    displayDate(date, format) {
      return moment(date).format(format);
    },
    onScrollToTime(time) {
      const id = `time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onRemoveSlots() {
      this.selectedAvailability = null;
      this.selectedSlots = [];
      this.selectedBookingId = 0;
    },
    onAddSlot(slot) {
      const slots = [...this.selectedSlots];
      const availability = this.availableTimeslots.find(
        i => i.unique === slot.unique
      );
      this.selectedAvailability = availability;

      if (slots.length) {
        const isDiffUnique = slots[0].unique !== slot.unique;
        if (isDiffUnique) {
          this.selectedSlots = [slot];
          return;
        }
      }

      let arr = [...slots].map(i => i.start_time_string);
      const value = slot.start_time_string;
      const times = this.clockHalf;
      const options = availability.slots.map(i => {
        return {
          value: i.start_time_string,
          disabled: i.appointment_id
        };
      });

      const remove = arr.includes(value);
      if (remove) {
        arr = arr.filter(i => i !== value);
      } else {
        arr.push(value);
      }
      arr.sort((a, b) => times.indexOf(a) - times.indexOf(b));
      if (arr.length > 1) {
        const firstIndex = times.indexOf(arr[0]);
        const lastIdx = times.indexOf(arr[arr.length - 1]);
        const valueIdx = times.indexOf(value);
        const currentIndex = lastIdx < valueIdx ? lastIdx : valueIdx;
        arr = times.filter(
          (_, idx) => idx >= firstIndex && idx <= currentIndex
        );
      }
      let hasDisabled = false;
      arr.forEach(i => {
        const opt = options.find(o => o.value === i);
        if (!opt) hasDisabled = true;
        else if (opt.disabled) hasDisabled = true;
      });
      if (hasDisabled) {
        arr = [value];
      }

      this.selectedSlots = availability.slots.filter(i =>
        arr.includes(i.start_time_string)
      );
    },
    onResumeBooking(slot) {
      const availability = this.availableTimeslots.find(
        i => i.unique === slot.unique
      );
      this.selectedAvailability = availability;
      this.selectedSlots = [slot.appointment];
      this.selectedBookingId = slot.appointment.booking_appointment_id;
      this.newRecurringOpened = true;
    },
    onCloseNewRecurring() {
      this.$emit("after-commit");
      this.newRecurringOpened = false;
    },
    //
    async fetchOrphanAppointments() {
      const params = {
        url: `/api/v1/appointments?page=1&per_page=1&user_id=&status=orphan`
      };
      const { success, data } = await this.actionGet(params);
      if (!success) return;
      this.totalOrphans = data.total_count;
      if (data.total_count > 0) {
        this.showOrphanBenner = true;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/timeslot/Timeslots.vue